/* City Memory Museum — the public timeline of things that actually happened in
   Bornwood's worlds: cases opened and closed, market shocks, cracked heists.
   The city writes its own history; this is where it's kept. */
import { useEffect, useState } from "react";
import { Landmark, FileSearch, Gavel, Zap, Vault, Megaphone, MapPin, CalendarDays } from "lucide-react";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";

const ICONS = { "file-search": FileSearch, gavel: Gavel, zap: Zap, vault: Vault, megaphone: Megaphone };
const KIND_COLORS = { mystery_opened: "text-amber-400 border-amber-500/30", mystery_solved: "text-emerald-400 border-emerald-500/30",
  market_shock: "text-sky-400 border-sky-500/30", heist_cracked: "text-red-400 border-red-500/30", bulletin: "text-zinc-300 border-white/15" };

export default function MuseumPage() {
  const [entries, setEntries] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/nc/memory/timeline").then((r) => setEntries(r.data.entries)).catch(() => setEntries([]));
  }, []);

  const servers = [...new Set((entries || []).map((e) => e.server_name))];
  const shown = (entries || []).filter((e) => !filter || e.server_name === filter);

  return (
    <div data-testid="museum-page" className="min-h-screen bg-[#05070a] text-zinc-100">
      <SiteNav />
      <div className="max-w-4xl mx-auto px-6 py-14">
        <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-nc-blue flex items-center gap-2">
          <Landmark className="w-4 h-4" /> The Bornwood Museum
        </p>
        <h1 className="font-bold uppercase tracking-wide text-2xl sm:text-4xl mt-3 leading-tight">City Memory</h1>
        <p className="font-mono text-xs text-zinc-400 mt-4 max-w-2xl leading-relaxed">
          The city remembers. Every closed case, every market shock, every vault that ever got cracked —
          written into the record the moment it happened.
        </p>

        {servers.length > 1 && (
          <div className="flex flex-wrap gap-2 mt-8">
            <button data-testid="museum-filter-all" onClick={() => setFilter("")}
              className={`font-mono text-[9px] uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors ${!filter ? "border-nc-blue text-nc-blue" : "border-white/15 text-zinc-500 hover:text-zinc-200"}`}>All worlds</button>
            {servers.map((s) => (
              <button key={s} data-testid={`museum-filter-${s.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setFilter(s)}
                className={`font-mono text-[9px] uppercase tracking-[0.15em] border px-3 py-1.5 transition-colors ${filter === s ? "border-nc-blue text-nc-blue" : "border-white/15 text-zinc-500 hover:text-zinc-200"}`}>{s}</button>
            ))}
          </div>
        )}

        {entries === null && <p className="font-mono text-xs text-zinc-500 mt-12 animate-pulse">Dusting off the exhibits…</p>}
        {entries?.length === 0 && (
          <div className="border border-dashed border-white/15 p-12 text-center mt-12">
            <Landmark className="w-8 h-8 text-zinc-600 mx-auto" />
            <p className="font-mono text-xs text-zinc-400 mt-4">Nothing in the museum yet — the city's history starts with its first case, shock or heist.</p>
          </div>
        )}

        <div className="mt-10 relative">
          <div className="absolute left-[7px] top-2 bottom-2 w-px bg-white/10" />
          <div className="flex flex-col gap-6">
            {shown.map((e) => {
              const Icon = ICONS[e.icon] || Megaphone;
              return (
                <div key={e.id} data-testid="museum-entry" className="relative pl-8">
                  <span className="absolute left-0 top-1.5 w-[15px] h-[15px] rounded-full border-2 bg-[#05070a] border-current" style={{ color: KIND_COLORS[e.kind]?.split(" ")[0]?.replace("text-", "") || "#71717a" }} />
                  <div className={`border bg-black/40 p-5 ${KIND_COLORS[e.kind]?.split(" ").slice(1).join(" ") || "border-white/10"}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Icon className={`w-3.5 h-3.5 ${KIND_COLORS[e.kind]?.split(" ")[0] || "text-zinc-400"}`} />
                      <span className={`font-mono text-[8px] uppercase tracking-[0.25em] ${KIND_COLORS[e.kind]?.split(" ")[0] || "text-zinc-400"}`}>{e.kind_label}</span>
                      <span className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.15em] flex items-center gap-1 ml-auto">
                        <MapPin className="w-3 h-3" /> {e.server_name} · <CalendarDays className="w-3 h-3" /> {new Date(e.at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="font-sans font-bold text-zinc-50 mt-2">{e.title}</p>
                    <p className="font-mono text-[10px] text-zinc-400 mt-1 leading-relaxed">{e.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
